import { type FetchBaseQueryError } from '@reduxjs/toolkit/query'

import api from 'api'
import {
  CACHE_TAG_NOTIFICATION_ITEMS,
  CACHE_TAG_NOTIFICATION_LIST,
  CACHE_TAG_NOTIFICATION_PREFERENCES,
  CACHE_TAG_NOTIFICATION_UNREAD_COUNT,
} from 'api/cacheTagTypes'
import {
  fromListNotificationItemsApi,
  fromListNotificationPreferencesApi,
  fromListNotificationsApi,
} from 'types/adapters/notification-center/notification'

import type {
  ListNotificationItemsApi,
  ListNotificationPreferencesApi,
  ListNotificationsApi,
  MarkReadResponseApi,
  UnreadCountApi,
} from 'types/api/notification-center/notification'
import type {
  NotificationItemListResponse,
  NotificationListParams,
  NotificationListResponse,
  NotificationPreference,
} from 'types/modules/notification-center/notification'

const BASE_URL = '/api/v1/core/notifications'

/** The controller clamps `size` to this. */
const MAX_PAGE_SIZE = 100

/**
 * Backstop on a runaway inbox. Nothing is near it today; if a caller ever passes it,
 * `totalItems` still reports the true size so the gap is visible rather than silent.
 */
const MAX_PAGES = 10

/** Mark-* rewrites `read_at`, which the list sorts on and the badge counts. */
const READ_STATE_TAGS = [CACHE_TAG_NOTIFICATION_LIST, CACHE_TAG_NOTIFICATION_UNREAD_COUNT]

type PageResult = { data?: unknown; error?: unknown }

type PageWalk<Page> = { ok: true; pages: Page[] } | { ok: false; error: unknown }

/**
 * Reads page 0 to learn the total, then pulls the rest together. Both list endpoints
 * default to 20 per page, which is smaller than either surface wants to show.
 */
export const fetchAllPages = async <Page extends { totalPages: number }>(
  fetchPage: (page: number) => PageResult | PromiseLike<PageResult>,
  adapt: (raw: unknown) => Page
): Promise<PageWalk<Page>> => {
  const firstResult = await fetchPage(0)
  if (firstResult.error) return { ok: false, error: firstResult.error }

  const first = adapt(firstResult.data)
  const remaining = Math.min(first.totalPages, MAX_PAGES) - 1
  if (remaining < 1) return { ok: true, pages: [first] }

  const rest = await Promise.all(Array.from({ length: remaining }, (_unused, index) => fetchPage(index + 1)))
  const failed = rest.find(result => result.error)
  if (failed) return { ok: false, error: failed.error }

  return { ok: true, pages: [first, ...rest.map(result => adapt(result.data))] }
}

const extendedApi = api.injectEndpoints({
  endpoints: builder => ({
    /**
     * Walks the endpoint's pages and hands back the caller's whole inbox.
     *
     * The views can't be pushed onto the query: "By type" groups several producers
     * into one CRM area (Billing alone spans four) and the contract filters on a
     * single `source`, so the area views, the toolbar's filter panel and the search
     * box all have to run over the complete set to be correct. Paging lazily would
     * make each of them true only as far as the user had scrolled.
     */
    getNotifications: builder.query<NotificationListResponse, NotificationListParams | void>({
      queryFn: async (params, _api, _extraOptions, fetchWithBQ) => {
        const shared = { ...(params ?? {}), size: MAX_PAGE_SIZE }
        const result = await fetchAllPages<NotificationListResponse>(
          page => fetchWithBQ({ url: BASE_URL, method: 'GET', params: { ...shared, page } }),
          raw => fromListNotificationsApi(raw as ListNotificationsApi)
        )
        if (!result.ok) return { error: result.error as FetchBaseQueryError }

        const [first] = result.pages
        return { data: { ...first, notifications: result.pages.flatMap(page => page.notifications) } }
      },
      providesTags: [CACHE_TAG_NOTIFICATION_LIST],
    }),

    getNotificationUnreadCount: builder.query<number, void>({
      query: () => ({ url: `${BASE_URL}/unread-count`, method: 'GET' }),
      transformResponse: (response: UnreadCountApi) => response.count,
      providesTags: [CACHE_TAG_NOTIFICATION_UNREAD_COUNT],
    }),

    /**
     * The newest unread, one page. The count tells the subscription that something changed but not
     * what, and `getNotifications` answers that by walking the whole inbox — far more than a probe
     * needs when only the top of the list can hold an arrival.
     */
    getLatestUnreadNotifications: builder.query<NotificationListResponse, { size: number }>({
      query: ({ size }) => ({ url: BASE_URL, method: 'GET', params: { read: false, page: 0, size } }),
      transformResponse: (response: ListNotificationsApi) => fromListNotificationsApi(response),
      providesTags: [CACHE_TAG_NOTIFICATION_LIST],
    }),

    getNotificationItems: builder.query<NotificationItemListResponse, { id: string }>({
      queryFn: async ({ id }, _api, _extraOptions, fetchWithBQ) => {
        const url = `${BASE_URL}/${id}/items`
        const result = await fetchAllPages<NotificationItemListResponse>(
          page => fetchWithBQ({ url, method: 'GET', params: { page, size: MAX_PAGE_SIZE } }),
          raw => fromListNotificationItemsApi(raw as ListNotificationItemsApi)
        )
        if (!result.ok) return { error: result.error as FetchBaseQueryError }

        const [first] = result.pages
        return { data: { ...first, items: result.pages.flatMap(page => page.items) } }
      },
      providesTags: (result, error, { id }) => [{ type: CACHE_TAG_NOTIFICATION_ITEMS, id }],
    }),

    getNotificationPreferences: builder.query<NotificationPreference[], void>({
      query: () => ({ url: `${BASE_URL}/preferences`, method: 'GET' }),
      transformResponse: (response: ListNotificationPreferencesApi) => fromListNotificationPreferencesApi(response),
      providesTags: [CACHE_TAG_NOTIFICATION_PREFERENCES],
    }),

    markNotificationsRead: builder.mutation<MarkReadResponseApi, { ids: string[] }>({
      query: ({ ids }) => ({ url: `${BASE_URL}/mark-read`, method: 'POST', body: { ids } }),
      invalidatesTags: READ_STATE_TAGS,
    }),

    markNotificationsUnread: builder.mutation<MarkReadResponseApi, { ids: string[] }>({
      query: ({ ids }) => ({ url: `${BASE_URL}/mark-unread`, method: 'POST', body: { ids } }),
      invalidatesTags: READ_STATE_TAGS,
    }),

    markAllNotificationsRead: builder.mutation<MarkReadResponseApi, void>({
      query: () => ({ url: `${BASE_URL}/mark-all-read`, method: 'POST' }),
      invalidatesTags: READ_STATE_TAGS,
    }),

    // A preference gates the producer, so turning one off changes what the list returns next.
    updateNotificationPreference: builder.mutation<NotificationPreference[], { prefKey: string; enabled: boolean }>({
      query: body => ({ url: `${BASE_URL}/preferences`, method: 'PUT', body }),
      transformResponse: (response: ListNotificationPreferencesApi) => fromListNotificationPreferencesApi(response),
      invalidatesTags: [CACHE_TAG_NOTIFICATION_PREFERENCES, CACHE_TAG_NOTIFICATION_LIST],
    }),
  }),
})

export const {
  useGetNotificationsQuery,
  useGetNotificationUnreadCountQuery,
  useGetLatestUnreadNotificationsQuery,
  useGetNotificationItemsQuery,
  useGetNotificationPreferencesQuery,
  useMarkNotificationsReadMutation,
  useMarkNotificationsUnreadMutation,
  useMarkAllNotificationsReadMutation,
  useUpdateNotificationPreferenceMutation,
} = extendedApi

export default extendedApi
